import {
  cloneDataOrNull, DataModel, DataModelBuilder, isSameData, isSameList,
  SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { PaymentMethod } from '../payment-method.js';
import { PaymentType } from '../payment-type.js';
import { VersionRange } from '../../core/version-range.js';
import { Language } from '../language.js';
import { Result } from '../../../utility/result.js';
import { OrderDomain } from '../order-domain.js';
import { OfflineReceiptType } from '../invoicing/offline-receipt-type.js';
import { CartCustomerDto } from './cart-customer.js';
import { ProductCartItemDto } from './product-cart-item.js';
import { ElectronicDeliveryMethod } from '../electronic-delivery-method.js';
import { OfflineCartBase } from './offline-cart-base.js';

export class OfflineProductCartDto extends DataModel implements OfflineCartBase {
  static readonly TYPE_ID: string = '92c14965a41d471ba7cc8699eaeb7598';
  static readonly SCHEMA_VERSION: number = 1;

  private constructor(
      public readonly businessRef: BusinessModuleRef,
      public readonly creatorId: string,
      public readonly items: ProductCartItemDto[],
      public readonly customerId: string | null,
      public readonly customer: CartCustomerDto | null,
      public readonly paymentType: PaymentType,
      public readonly paymentMethod: PaymentMethod,
      public readonly language: Language,
      public readonly domain: OrderDomain,
      public readonly note: string | null,
      public readonly deliveryMethod: ElectronicDeliveryMethod,
      public readonly allowOfflineOrderReceipts: boolean | null,
      public readonly receiptType: OfflineReceiptType | null,
  ) {
    super();
  }

  get $typeId() {
    return OfflineProductCartDto.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    // Version should always be `exactly`.
    // Cart should never be backwards migrated, backend should be updated before frontend.
    return VersionRange.exactly(OfflineProductCartDto.SCHEMA_VERSION);
  }

  static create(args: {
      businessRef: BusinessModuleRef,
      creatorId: string,
      items: ProductCartItemDto[],
      customerId: string | null,
      customer: CartCustomerDto | null,
      paymentType: PaymentType,
      paymentMethod: PaymentMethod,
      language: Language,
      domain: OrderDomain,
      note: string | null,
      deliveryMethod: ElectronicDeliveryMethod,
      allowOfflineOrderReceipts: boolean | null,
      receiptType: OfflineReceiptType | null,
  }): OfflineProductCartDto {
    return new OfflineProductCartDto(
        args.businessRef,
        args.creatorId,
        args.items,
        args.customerId,
        args.customer,
        args.paymentType,
        args.paymentMethod,
        args.language,
        args.domain,
        args.note,
        args.deliveryMethod,
        args.allowOfflineOrderReceipts,
        args.receiptType,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineProductCartDto {
    return this.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      items: MapUtils.getList<ProductCartItemDto>(map, 'items', (e) => {
        return context.fromData(e as SerializedData);
      }),
      creatorId: MapUtils.getString(map, 'creatorId'),
      customerId: MapUtils.getStringOrNull(map, 'customerId'),
      customer: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'customer')),
      paymentType: toEnumFromString(MapUtils.getString(map, 'paymentType'), PaymentType),
      paymentMethod: toEnumFromString(MapUtils.getString(map, 'paymentMethod'), PaymentMethod),
      language: toEnumFromString(MapUtils.getString(map, 'language'), Language),
      domain: toEnumFromString(MapUtils.getString(map, 'domain'), OrderDomain),
      note: MapUtils.getStringOrNull(map, 'note'),
      deliveryMethod: toEnumFromString(MapUtils.getString(map, 'deliveryMethod'), ElectronicDeliveryMethod),
      allowOfflineOrderReceipts: MapUtils.getBooleanOrNull(map, 'allowOfflineOrderReceipts'),
      receiptType: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'receiptType'), OfflineReceiptType),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'items': this.items.map((e) => context.toData(e)),
      'creatorId': this.creatorId,
      'customerId': this.customerId,
      'customer': context.toDataOrNull(this.customer),
      'paymentType': fromEnumAsString(this.paymentType),
      'paymentMethod': fromEnumAsString(this.paymentMethod),
      'language': fromEnumAsString(this.language),
      'domain': fromEnumAsString(this.domain),
      'note': this.note,
      'deliveryMethod': fromEnumAsString(this.deliveryMethod),
      'allowOfflineOrderReceipts': this.allowOfflineOrderReceipts,
      'receiptType': fromEnumAsStringOrNull(this.receiptType),
    };
  }

  clone(): this {
    return OfflineProductCartDto.create({
      businessRef: this.businessRef.clone(),
      items: this.items.map((e) => e.clone()),
      creatorId: this.creatorId,
      customerId: this.customerId,
      customer: cloneDataOrNull(this.customer),
      paymentType: this.paymentType,
      paymentMethod: this.paymentMethod,
      language: this.language,
      domain: this.domain,
      note: this.note,
      deliveryMethod: this.deliveryMethod,
      allowOfflineOrderReceipts: this.allowOfflineOrderReceipts,
      receiptType: this.receiptType,
    }) as this;
  }

  isSame(other: OfflineProductCartDto): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      isSameList(other.items, this.items, isSameData) &&
      other.creatorId === this.creatorId &&
      other.customerId === this.customerId &&
      other.paymentType === this.paymentType &&
      other.paymentMethod === this.paymentMethod &&
      other.language === this.language &&
      other.domain === this.domain &&
      other.note === this.note &&
      other.deliveryMethod === this.deliveryMethod &&
      other.allowOfflineOrderReceipts === this.allowOfflineOrderReceipts &&
      other.receiptType === this.receiptType &&
      isSameData(other.customer, this.customer);
  }

  findUniqueEventId(): Result<string | null, 'multiple-events-found'> {
    let eventId: string | null = null;

    for(const entry of this.items) {
      if(entry.eventId != null) {
        if(eventId != null && entry.eventId !== eventId) {
          return Result.error('multiple-events-found');
        }

        eventId = entry.eventId;
      }
    }

    return Result.ok(eventId);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof OfflineProductCartDto;
  }

  static assertType(order: DataModel): order is OfflineProductCartDto {
    return order instanceof OfflineProductCartDto;
  }
}

export class OfflineProductCartDtoBuilder extends DataModelBuilder<OfflineProductCartDto> {
  build(context: SerializationContext, map: SerializedData): OfflineProductCartDto {
    return OfflineProductCartDto.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OfflineProductCartDto.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OfflineProductCartDto.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    const version = versionRange.to;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
